'use client';

import { useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { ComboBox } from '@components/common/Forms';
import { Chip } from '@components/common';
import { ApiLocationTooltipOk } from '@type/v1-api-types/common';
import { useLocationsBuilder } from '@hooks/form-helpers/useLocationsBuilder';

interface CandidateJobSearchLocationProps {
  onChange: (locations: string[]) => void;
  initialLocations?: string[];
}

/**
 * @description Location input field for the candidate job search form.
 * Uses the locations builder hook for autocomplete and pill management.
 */
export const CandidateJobSearchLocation = ({ onChange, initialLocations }: CandidateJobSearchLocationProps) => {
  const { t } = useTranslation('common');
  const hasSeededInitialLocations = useRef(false);

  const {
    comboboxOptions,
    selectedLocations,
    addLocation,
    deleteLocation,
  } = useLocationsBuilder();

  // Seed initial locations on mount (no onChange — parent already has these values)
  useEffect(() => {
    if (hasSeededInitialLocations.current || !initialLocations?.length) {
      return;
    }

    hasSeededInitialLocations.current = true;
    initialLocations?.forEach((loc) => addLocation(loc));
  }, [addLocation, initialLocations]);

  const handleAddLocation = (location: string) => {
    addLocation(location);
    onChange([...selectedLocations, location]);
  };

  const handleDeleteLocation = (location: string) => {
    deleteLocation(location);
    onChange(selectedLocations.filter((l) => l !== location));
  };

  return (
    <div className="grid gap-2">
      <div className="relative">
        <MapPinIcon className="pointer-events-none absolute left-4 top-1/2 z-1 size-5 -translate-y-1/2 text-ink-medium" />
        <ComboBox
          name="candidate_search_location"
          id="candidate_search_location"
          minLength={3}
          placeholder={t('cv-value-label-city-placeholder')}
          className="h-12 w-full rounded-2xl border-input/65 bg-surface !pl-12 text-base text-ink placeholder:text-ink-light focus:!border-secondary focus:!ring-secondary/20 md:text-sm"
          dropdownContainerClass="z-9 bottom-full mb-2 max-h-64 rounded-2xl border border-input/60 bg-surface py-2 shadow-lg overflow-hidden"
          options={{
            ...comboboxOptions,
            clearOnSelect: true,
            showDropdownButton: false,
            showResetButton: false,
          }}
          renderList={(item, index, listKey, getItemProps, listStyling, highlightedIndex) => {
            const location = item as ApiLocationTooltipOk;
            return (
              <li
                key={listKey}
                className={`${listStyling} !cursor-pointer flex-col !py-2.5 !pl-5`}
                {...getItemProps({ item, index })}
              >
                <span className="text-sm">{location.locality}</span>
                <span className={`-mt-0.5 text-xs ${highlightedIndex === index ? 'text-white/80' : 'text-ink-medium'}`}>
                  {location.country}
                </span>
              </li>
            );
          }}
          onSelectedItemChange={(item) => {
            const location = item as ApiLocationTooltipOk;
            if (location?.locality) {
              handleAddLocation(location.locality);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        />
      </div>

      {!!selectedLocations.length && (
        <div className="flex flex-wrap gap-2">
          {selectedLocations.map((location) => (
            <Chip key={location} onDelete={() => handleDeleteLocation(location)}>
              {location}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
};
